import React from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import {MOLECULE_LOT_FIELD_NAMES} from './MoleculeLotConstants';

export interface MoleculeLotFieldBannersProps {
  /** Whether the molecule lot SSI checkbox is checked. */
  isMoleculeLotSelected: boolean;
  /** Whether this is a new schema being created (not editing an existing one). */
  isCreating: boolean;
  /** Whether default units were auto-filled on the molar concentration field. */
  molarUnitsApplied?: boolean;
  /** Whether default units were auto-filled on the density concentration field. */
  densityUnitsApplied?: boolean;
}

/**
 * Renders contextual banners for the Molecule Lot schema configuration flow.
 *
 * Shows either:
 * - A confirmation that default units were set (if both auto-filled)
 * - A partial message naming the field that still needs configuration (if only one auto-filled)
 * - A prompt to manually configure units (if auto-fill was not possible for either)
 */
export default function MoleculeLotFieldBanners({
  isMoleculeLotSelected,
  isCreating,
  molarUnitsApplied,
  densityUnitsApplied,
}: MoleculeLotFieldBannersProps): JSX.Element | null {
  if (!isCreating || !isMoleculeLotSelected) {
    return null;
  }

  const bothApplied = molarUnitsApplied && densityUnitsApplied;
  const eitherApplied = molarUnitsApplied || densityUnitsApplied;

  if (bothApplied) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.SUCCESS}
        text={
          'Molecule lot fields have been added with default concentration units. ' +
          'You can change the units via the advanced settings (gear icon) on each field.'
        }
      />
    );
  }

  if (eitherApplied) {
    const missingField = !molarUnitsApplied
      ? MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_MOLAR.displayName
      : MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_DENSITY.displayName;
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={
          `Molecule lot fields have been added. A default unit was set on one concentration field, ` +
          `but no matching unit type was found for "${missingField}". ` +
          `Please configure its unit via the advanced settings (gear icon).`
        }
      />
    );
  }

  return (
    <InlineBanner
      bannerType={InlineBannerType.INFO}
      text={
        'Molecule lot fields have been added. Please configure units on the concentration fields: ' +
        'select a molar concentration unit (e.g. M, mM) for "Concentration (molar)" and a ' +
        'mass concentration unit (e.g. g/L, mg/mL) for "Concentration (density)". ' +
        'You can also set a percent unit on the "Purity" field. ' +
        'Use the advanced settings (gear icon) on each field to set the unit.'
      }
    />
  );
}
